<script setup lang="ts">
import AlertIcon from '@/assets/icons/alert.svg?component'
import type { SiteStatus } from '@/contracts/site.contract'

/**
 * 站台維護／停用時的整頁畫面。
 *
 * 蓋掉整個 app 而不是在頁面上加一條橫幅：維護中時**每一支 API 都是 503**，
 * 讓使用者進得去只會看到一堆載入失敗，還會一直去點按不動的按鈕。
 *
 * 維護與停用的文案分開。兩者對使用者的意義完全不同 ——
 * 維護是「等一下回來」，停用是「這個站不做了」。
 * 用同一句話帶過會讓等得到的人放棄、等不到的人一直重整。
 *
 * 展示層元件：狀態由 props 傳入，重試往上拋。
 */
defineProps<{
  status: Exclude<SiteStatus, 'active'>
  siteName: string
  retrying: boolean
}>()

defineEmits<{ retry: [] }>()
</script>

<template>
  <div class="blocked">
    <div class="blocked__card">
      <AlertIcon class="blocked__icon" />

      <h1 class="blocked__title">{{ $t(`site.${status}.title`) }}</h1>
      <p class="blocked__body">{{ $t(`site.${status}.body`) }}</p>
      <p class="blocked__site">{{ siteName }}</p>

      <!-- 只有維護中才給重試：停用的站再按幾次都不會回來 -->
      <button
        v-if="status === 'maintenance'"
        type="button"
        class="blocked__retry"
        :disabled="retrying"
        @click="$emit('retry')"
      >
        {{ retrying ? $t('common.loading') : $t('common.retry') }}
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.blocked {
  display: grid;
  place-items: center;
  // `dvh` 而不是 `vh`：手機的 `100vh` 是網址列收起來時的高度，
  // 用它會多出一段捲不到底的空白（前一行是舊瀏覽器的退路）
  min-height: 100vh;
  min-height: 100dvh;
  padding: $space-6;
  background: $color-bg;
  color: $color-text;

  &__card {
    // 面板色往下傳，讓共用元件在這裡也走亮色
    @include panel-surface;

    width: 100%;
    max-width: 420px;
    padding: $space-8 $space-6;
    border: 1px solid $color-panel-border;
    border-radius: $radius-lg;
    background: $color-panel;
    box-shadow: $shadow-lg;
    text-align: center;
  }

  &__icon {
    width: 48px;
    height: 48px;
    color: $color-warning;
  }

  &__title {
    margin: $space-4 0 $space-2;
    font-size: $text-xl;
    font-weight: 700;
  }

  &__body {
    margin: 0;
    color: $color-text-dim;
    font-size: $text-base;
    line-height: 1.7;
  }

  &__site {
    margin: $space-5 0 0;
    color: $color-text-muted;
    font-size: $text-sm;
  }

  &__retry {
    margin-top: $space-5;
    padding: $space-3 $space-6;
    border: 0;
    border-radius: $radius-full;
    background: $color-action;
    color: $color-on-action;
    font-family: inherit;
    font-size: $text-base;
    font-weight: 700;
    cursor: pointer;
    transition: background-color $transition-fast;

    &:hover:not(:disabled) {
      background: $color-action-hover;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
</style>
